import { Tag } from "@faire/slate/components/tag";
import { useMemo, useState } from "react";

import { Search } from "../../../../components/design-system";
import { COPY } from "../copy";
import {
  PRODUCT_ROWS,
  REAL_BRAND_PRODUCT_ROWS,
  type ProductRow,
} from "../mockData";
import { getActiveProfileToken } from "../p8AdvertiserData";
import type { ProductDateRange, ProductStatus } from "../types";

import { AdsManagerContent } from "./AdsManagerContent";
import {
  useCardPaddingClass,
  useIsCompact,
  useTableRowHeightClass,
} from "./CardDensity";
import { InsightsFilterMenu } from "./InsightsFilterMenu";
import { TablePagination } from "./TablePagination";

const PLACEHOLDER_IMAGE_URL =
  "https://cdn.faire.com/fastly/cd023b381307ed3e3705704a6b6136873b3f683f4746a4a82e3810e3339b8d6b.png";

const PAGE_SIZE = 10;

const DATE_RANGE_OPTIONS = [
  { value: "this-month" as const, label: "This month" },
  { value: "this-quarter" as const, label: "This quarter" },
  { value: "this-year" as const, label: "This year" },
  { value: "past-30-days" as const, label: "Past 30 days" },
  { value: "past-90-days" as const, label: "Past 90 days" },
  { value: "past-12-months" as const, label: "Past 12 months" },
  { value: "lifetime" as const, label: "Lifetime" },
];

// `triggerLabel` keeps the collapsed button short ("Products: All") while the
// menu itself spells the options out.
const STATUS_OPTIONS = [
  { value: "all" as const, label: "All products", triggerLabel: "All" },
  {
    value: "boosted" as const,
    label: "Boosted products",
    triggerLabel: "Boosted",
  },
  {
    value: "advertising" as const,
    label: "Advertising products",
    triggerLabel: "Advertising",
  },
  {
    value: "excluded" as const,
    label: "Excluded products",
    triggerLabel: "Excluded",
  },
  {
    value: "unpublished" as const,
    label: "Unpublished products",
    triggerLabel: "Unpublished",
  },
  { value: "draft" as const, label: "Drafts", triggerLabel: "Drafts" },
];

// The rows carry trailing-12-month totals, so "Past 12 months" is the unscaled
// base and every other range is a fraction of it.
const DATE_RANGE_SCALE: Record<ProductDateRange, number> = {
  "this-month": 0.06,
  "this-quarter": 0.24,
  "this-year": 0.62,
  "past-30-days": 0.08,
  "past-90-days": 0.25,
  "past-12-months": 1,
  lifetime: 1.24,
};

function SortIcon({ direction }: { direction: SortDirection }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="shrink-0 text-[#333]"
      aria-hidden
    >
      {direction === "asc" ? (
        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
      ) : (
        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
      )}
    </svg>
  );
}

function SortableHeader({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  className,
  align = "left",
}: {
  label: string;
  column: ProductSortColumn;
  activeColumn: ProductSortColumn;
  direction: SortDirection;
  onSort: (column: ProductSortColumn) => void;
  className?: string;
  align?: "left" | "right";
}) {
  const isActive = activeColumn === column;
  const isRight = align === "right";

  return (
    <th
      className={`${className ?? ""} whitespace-nowrap ${isRight ? "text-right" : ""}`}
      aria-sort={
        isActive ? (direction === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1 ${
          isRight ? "w-full justify-end" : "text-left"
        }`}
        onClick={() => onSort(column)}
      >
        <span
          className={
            isActive
              ? "faire-label-medium text-[#333]"
              : "faire-label-medium text-[#333] hover:underline"
          }
        >
          {label}
        </span>
        {isActive ? <SortIcon direction={direction} /> : null}
      </button>
    </th>
  );
}

function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="3" cy="8" r="1" fill="#333" />
      <circle cx="8" cy="8" r="1" fill="#333" />
      <circle cx="13" cy="8" r="1" fill="#333" />
    </svg>
  );
}

function ProductThumbnail({ color }: { color: string }) {
  return (
    <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-sm bg-[#f7f7f7]">
      <img
        src={PLACEHOLDER_IMAGE_URL}
        alt=""
        className="h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div
        className="absolute inset-0"
        style={{ backgroundColor: color, opacity: 0.45 }}
        aria-hidden
      />
    </div>
  );
}

function formatDateAdded(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

function isWithinLast60Days(
  isoDate: string,
  referenceDate = new Date()
): boolean {
  const [year, month, day] = isoDate.split("-").map(Number);
  const added = new Date(year, month - 1, day);
  const cutoff = new Date(referenceDate);
  cutoff.setDate(cutoff.getDate() - 60);
  cutoff.setHours(0, 0, 0, 0);
  added.setHours(0, 0, 0, 0);
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  return added >= cutoff && added <= today;
}

function isBoostedProduct(
  row: ProductRow,
  newProductsBoosting: boolean
): boolean {
  return (
    newProductsBoosting &&
    row.status === "advertising" &&
    isWithinLast60Days(row.dateAdded)
  );
}

function statusTag(row: ProductRow, newProductsBoosting: boolean) {
  if (isBoostedProduct(row, newProductsBoosting)) {
    return <Tag label="Boosted" variant="success" />;
  }

  switch (row.status) {
    case "advertising":
      return <Tag label="Advertising" variant="info" />;
    case "excluded":
      return <Tag label="Excluded" variant="warning" />;
    case "unpublished":
      return <Tag label="Unpublished" variant="neutral" />;
    case "draft":
      return <Tag label="Draft" variant="neutral" />;
  }
}

function parseCount(value: string) {
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCount(value: number) {
  return Math.max(0, Math.round(value)).toLocaleString("en-US");
}

function scaleMetricValue(value: string, scale: number) {
  if (value === "—") {
    return value;
  }
  if (value.endsWith("%")) {
    return value;
  }
  const parsed = parseCount(value);
  return formatCount(parsed * scale);
}

function matchesStatusFilter(
  row: ProductRow,
  status: ProductStatus,
  newProductsBoosting: boolean
) {
  if (status === "all") {
    return true;
  }
  if (status === "boosted") {
    return isBoostedProduct(row, newProductsBoosting);
  }
  return row.status === status;
}

type ProductSortColumn =
  | "name"
  | "status"
  | "sku"
  | "dateAdded"
  | "wholesalePrice"
  | "impressions"
  | "ctr"
  | "clicks";

type SortDirection = "asc" | "desc";

function parsePercent(value: string) {
  if (value === "—") {
    return -1;
  }
  return Number.parseFloat(value.replace("%", "")) || 0;
}

function parsePrice(value: string) {
  return Number.parseFloat(value.replace(/[$,]/g, "")) || 0;
}

function getStatusSortLabel(
  row: ProductRow,
  newProductsBoosting: boolean
): string {
  if (isBoostedProduct(row, newProductsBoosting)) {
    return "boosted";
  }
  return row.status;
}

function compareProductRows(
  left: ProductRow,
  right: ProductRow,
  column: ProductSortColumn,
  direction: SortDirection,
  newProductsBoosting: boolean
): number {
  const multiplier = direction === "asc" ? 1 : -1;

  switch (column) {
    case "name":
      return multiplier * left.name.localeCompare(right.name);
    case "status":
      return (
        multiplier *
        getStatusSortLabel(left, newProductsBoosting).localeCompare(
          getStatusSortLabel(right, newProductsBoosting)
        )
      );
    case "sku":
      return multiplier * left.sku.localeCompare(right.sku);
    case "dateAdded":
      return multiplier * left.dateAdded.localeCompare(right.dateAdded);
    case "wholesalePrice":
      return (
        multiplier *
        (parsePrice(left.wholesalePrice) - parsePrice(right.wholesalePrice))
      );
    case "impressions":
      return (
        multiplier *
        (parseCount(left.impressions) - parseCount(right.impressions))
      );
    case "ctr":
      return multiplier * (parsePercent(left.ctr) - parsePercent(right.ctr));
    case "clicks":
      return multiplier * (parseCount(left.clicks) - parseCount(right.clicks));
    default:
      return 0;
  }
}

export function ProductsTab({
  newProductsBoosting,
}: {
  newProductsBoosting: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<ProductDateRange>("past-90-days");
  const [status, setStatus] = useState<ProductStatus>("all");
  const [sortColumn, setSortColumn] = useState<ProductSortColumn>("clicks");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const cardPaddingClass = useCardPaddingClass();
  const rowHeightClass = useTableRowHeightClass();
  const isCompact = useIsCompact();
  // V3 right-aligns the money and count columns so the figures line up.
  const figureAlign = isCompact ? "right" : "left";
  const figureCellClass = isCompact ? "text-right" : "";
  const profileToken = getActiveProfileToken();

  const handleSort = (column: ProductSortColumn) => {
    // Re-sorting reshuffles the whole list, so the current page number no
    // longer refers to the same products.
    setPage(1);

    if (sortColumn === column) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection(
      column === "name" || column === "sku" || column === "status"
        ? "asc"
        : "desc"
    );
  };

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const scale = DATE_RANGE_SCALE[dateRange];
    // Real-brand variants show their own catalog and the platform's own
    // per-product metrics; everyone else falls back to the generic rows.
    const rows = REAL_BRAND_PRODUCT_ROWS[profileToken] ?? PRODUCT_ROWS;

    return rows
      .filter((row) => {
        if (!matchesStatusFilter(row, status, newProductsBoosting)) {
          return false;
        }
        if (!query) {
          return true;
        }
        return (
          row.name.toLowerCase().includes(query) ||
          row.sku.toLowerCase().includes(query)
        );
      })
      .map((row) => ({
        ...row,
        impressions: scaleMetricValue(row.impressions, scale),
        clicks: scaleMetricValue(row.clicks, scale),
      }))
      .sort((left, right) =>
        compareProductRows(
          left,
          right,
          sortColumn,
          sortDirection,
          newProductsBoosting
        )
      );
  }, [
    dateRange,
    newProductsBoosting,
    profileToken,
    searchQuery,
    sortColumn,
    sortDirection,
    status,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <AdsManagerContent className="py-8 pb-32">
      <div className="space-y-6">
        {/* The date range sits with the heading, matching the Overview and
            Customers tabs, so the one control every tab shares is always in the
            same place. */}
        <div className="flex flex-col gap-4 min-[733px]:flex-row min-[733px]:items-start min-[733px]:justify-between">
          <div>
            <h2 className="text-xl leading-8 font-medium text-[#333]">
              {COPY.products.heading}
            </h2>
            <p className="max-w-[720px] text-sm leading-5 text-[#757575]">
              {isCompact ? COPY.products.v3Subhead : COPY.products.subhead}
            </p>
          </div>
          <div className="shrink-0">
            <InsightsFilterMenu
              name="products-date-range"
              labelPrefix="Date range"
              menuTitle="Filter by date range"
              value={dateRange}
              options={DATE_RANGE_OPTIONS}
              onChange={setDateRange}
              applyToConfirm
            />
          </div>
        </div>

        <div
          className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
        >
          <div className="mb-4 flex flex-col gap-3 min-[733px]:flex-row min-[733px]:items-center min-[733px]:justify-between">
            <div className="flex min-w-0 flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
              <div className="w-full min-[733px]:w-[348px]">
                <Search
                  placeholder="Search by product name or SKU"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  onClear={() => {
                    setSearchQuery("");
                    setPage(1);
                  }}
                />
              </div>
              {/* Stays with the search: it narrows which products are listed
                  rather than which period they're measured over. */}
              <InsightsFilterMenu
                name="products-status"
                labelPrefix="Products"
                menuTitle="Filter by status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
              />
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                className="text-sm text-[#333] underline decoration-[#333] underline-offset-2"
              >
                About these results
              </button>
              <button
                type="button"
                className="bg-fs-surface-primary inline-flex h-10 w-10 items-center justify-center rounded border border-[#dfe0e1]"
                aria-label="More actions"
              >
                <MoreIcon />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed border-collapse text-left text-sm">
              {/* Pinned widths, so paging doesn't reshuffle the columns: a
                  `table-auto` table sizes each one to the widest cell on the
                  current page, and product names and figures vary a lot in
                  length from page to page.

                  Each share is at least what its header needs on one line at
                  the table's 1000px minimum — "Click-through rate" is the
                  widest at 155px — and product name gives up the difference,
                  since its values already clip. */}
              <colgroup>
                <col style={{ width: "21%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12.5%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "10.5%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-[#dfe0e1]">
                  <SortableHeader
                    label="Product name"
                    column="name"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="min-w-[128px] px-4 py-3"
                  />
                  <SortableHeader
                    label="Status"
                    column="status"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="min-w-[80px] px-4 py-3"
                  />
                  <SortableHeader
                    label="SKU"
                    column="sku"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="min-w-[64px] px-4 py-3"
                  />
                  <SortableHeader
                    label="WS price"
                    column="wholesalePrice"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align={figureAlign}
                    className="min-w-[96px] px-4 py-3"
                  />
                  <SortableHeader
                    label="Date added"
                    column="dateAdded"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="min-w-[112px] px-4 py-3"
                  />
                  <SortableHeader
                    label="Impressions"
                    column="impressions"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align={figureAlign}
                    className="min-w-[120px] px-4 py-3"
                  />
                  <SortableHeader
                    label="Click-through rate"
                    column="ctr"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align={figureAlign}
                    className="min-w-[160px] px-4 py-3"
                  />
                  <SortableHeader
                    label="Clicks"
                    column="clicks"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align={figureAlign}
                    className="min-w-[96px] px-4 py-3"
                  />
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-sm text-[#757575]"
                    >
                      No products match your filters.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-b border-[#dfe0e1] last:border-0 hover:bg-[#fafafa] ${rowHeightClass}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <ProductThumbnail color={row.imageColor} />
                          <button
                            type="button"
                            className="max-w-[280px] truncate text-left text-sm font-medium text-[#333] hover:underline"
                          >
                            {row.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {statusTag(row, newProductsBoosting)}
                      </td>
                      <td className="px-4 py-3 text-[#333]">{row.sku}</td>
                      <td
                        className={`px-4 py-3 text-[#333] ${figureCellClass}`}
                      >
                        {row.wholesalePrice}
                      </td>
                      <td className="px-4 py-3 text-[#333]">
                        {formatDateAdded(row.dateAdded)}
                      </td>
                      <td
                        className={`px-4 py-3 text-[#333] ${figureCellClass}`}
                      >
                        {row.impressions}
                      </td>
                      <td
                        className={`px-4 py-3 text-[#333] ${figureCellClass}`}
                      >
                        {row.ctr}
                      </td>
                      <td
                        className={`px-4 py-3 text-[#333] ${figureCellClass}`}
                      >
                        {row.clicks}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="mt-4 flex justify-center">
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onClick={setPage}
              />
            </div>
          ) : null}
        </div>
      </div>
    </AdsManagerContent>
  );
}
